import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import IntlService from 'ember-intl/services/intl';

interface IArgs {
  currentPage?: number;
  totalPages: number;
  currentPageSize: number;
  totalCount: number;
  paginatorSize?: 'md' | 'sm';
  pageChanged?: (current: number, previous: number) => void;
  pageSizeChanged?: (size: number) => void;
  condensed: boolean;
  displayPagination: boolean;
  pageSizes?: number[];
}

export default class BvPagination extends Component<IArgs> {
  @service intl!: IntlService;

  get pageSizes() {
    return this.args.pageSizes ?? [];
  }

  get displayPagination() {
    return this.args.displayPagination ?? true;
  }

  get paginatorSize() {
    return this.args.paginatorSize ?? 'md';
  }

  get currentPage() {
    return this.args.currentPage ?? 1;
  }

  get pages() {
    const { totalPages } = this.args;
    let adjusted = totalPages <= 0 ? 1 : totalPages;
    return Array(...Array(adjusted)).map((_, i) => i + 1);
  }

  get start() {
    return this.args.currentPageSize * (this.currentPage - 1) + 1;
  }

  get end() {
    return this.args.currentPageSize * this.currentPage;
  }

  get displayedPageSize() {
    const { totalCount, currentPageSize } = this.args;
    return totalCount > currentPageSize ? currentPageSize : totalCount;
  }

  get displayPrevious() {
    return this.currentPage > 1;
  }

  get displayNext() {
    return this.end < this.args.totalCount;
  }

  get nextPage() {
    return this.displayNext && this.intl.t('pageNumber', { number: this.currentPage + 1 });
  }

  get previousPage() {
    return this.displayPrevious && this.intl.t('pageNumber', { number: this.currentPage - 1 });
  }

  @action
  handlePreviousPage() {
    const { currentPage } = this;
    this.args.pageChanged?.(currentPage - 1, currentPage);
  }
  @action
  handleNextPage() {
    const { currentPage } = this;
    this.args.pageChanged?.(currentPage + 1, currentPage);
  }
  @action
  handlePageChanged(page: number, previous: number) {
    this.args.pageChanged?.(page, previous);
  }

  @action
  handlePageSizeChanged(size: number) {
    this.args.pageSizeChanged?.(size);
  }
}
